import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../lib/config';
import { AuthCard, Field, inputClass, primaryButtonClass, ErrorBanner, NoticeBanner } from '../components/AuthCard';
import { useAuthCapabilities } from '../hooks/useAuthCapabilities';

export default function ForgotPassword() {
  const caps = useAuthCapabilities();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/request-password-reset`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          redirectTo: `${window.location.origin}/reset-password`,
        }),
      });
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      if (!res.ok) {
        setError(body?.message || 'Could not send reset email.');
        return;
      }
      setSuccess(body?.message || 'If this email exists, a reset link has been sent.');
    } catch (err) {
      setError((err as Error).message || 'Could not send reset email.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Reset password"
      subtitle="Get a reset link by email when password reset delivery is configured."
      footer={<Link to="/login" className="font-semibold text-amber-700 hover:text-amber-800 hover:underline">Back to sign in</Link>}
    >
      <ErrorBanner message={error} />
      {success && <NoticeBanner tone="emerald" className="mb-4">{success}</NoticeBanner>}
      {caps && !caps.passwordResetEnabled ? (
        <NoticeBanner tone="amber">
          Password reset email isn’t configured yet for this environment.
        </NoticeBanner>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Email">
            <input
              type="email"
              required
              autoComplete="email"
              autoFocus
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={inputClass}
            />
          </Field>
          <button type="submit" disabled={submitting} className={primaryButtonClass}>
            {submitting ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}
    </AuthCard>
  );
}
